import { inject, Injectable } from '@angular/core';
import { BrowserPlatformService } from './browser-platform.service';

export type ListingFilterValue = string | number | boolean | null | ListingFilterValue[];
export type ListingFilterState = Record<string, ListingFilterValue>;

const STORAGE_PREFIX = 'ccms.listing-filters.';
const OWNER_KEY = `${STORAGE_PREFIX}owner`;

@Injectable({ providedIn: 'root' })
export class ListingFilterStateService {
  private readonly browser = inject(BrowserPlatformService);

  beginUserSession(userId: number) {
    const owner = this.browser.getSessionStorageItem(OWNER_KEY);
    const nextOwner = userId.toString();

    if (owner !== null && owner !== nextOwner) {
      this.clear();
    }

    this.browser.setSessionStorageItem(OWNER_KEY, nextOwner);
  }

  load<T extends ListingFilterState>(key: string, defaults: T): T {
    if (!this.hasActiveSession()) {
      return { ...defaults };
    }

    const raw = this.browser.getSessionStorageItem(this.storageKey(key));
    if (!raw) {
      return { ...defaults };
    }

    try {
      const stored = JSON.parse(raw) as Record<string, unknown>;
      const restored = { ...defaults };

      for (const field of Object.keys(defaults) as Array<keyof T>) {
        const value = stored[field as string];
        if (this.isCompatibleValue(value, defaults[field])) {
          restored[field] = value as T[keyof T];
        }
      }

      return restored;
    } catch {
      return { ...defaults };
    }
  }

  save<T extends ListingFilterState>(key: string, state: T) {
    if (!this.hasActiveSession()) {
      return;
    }

    this.browser.setSessionStorageItem(this.storageKey(key), JSON.stringify(state));
  }

  clear() {
    for (const key of this.browser.sessionStorageKeys()) {
      if (key.startsWith(STORAGE_PREFIX)) {
        this.browser.removeSessionStorageItem(key);
      }
    }
  }

  private hasActiveSession() {
    return this.browser.getSessionStorageItem(OWNER_KEY) !== null;
  }

  private storageKey(key: string) {
    return `${STORAGE_PREFIX}${key}`;
  }

  private isCompatibleValue(value: unknown, fallback: ListingFilterValue): boolean {
    if (value === null || fallback === null) {
      return value === null || ['string', 'number', 'boolean'].includes(typeof value);
    }

    if (Array.isArray(fallback)) {
      return Array.isArray(value);
    }

    return typeof value === typeof fallback;
  }
}
